import React, { useState, useEffect } from 'react';
import { useFamilyAssistances, useDeleteFamilyAssistance, FamilyAssistance } from "#root/queries/familyAssistance";
import { Breadcrumbs } from '#root/components/Breadcrumbs';
import { useNavigate } from 'react-router-dom';
import { showToast } from "#root/components/GlobalToaster";
import { DialogBox } from "#root/components/DialogBox";
import { Button, Field, Input } from "@fluentui/react-components";
import Paginator from "#root/components/Paginator";
import LoadingWrapper from "#root/components/LoadingWrapper";
import './styles.scss';
import { Edit20Filled, Delete20Filled, SearchFilled, CalendarPersonRegular } from '@fluentui/react-icons';
import LazyImage from '#root/components/LazyImage';
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import { useInfiniteIncidents, Incident } from "#root/queries/incidents";

const INCIDENTS_PER_PAGE = 10;

const FamilyAssistanceEntries: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIncidentId, setSelectedIncidentId] = useState<string>('');
  const [incidentFilter, setIncidentFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const storedIncident = localStorage.getItem('vadminIncident');
    if (storedIncident) {
      setSelectedIncidentId(storedIncident);
    }
  }, []);

  const { data, isLoading, refetch } = useFamilyAssistances({
    searchQuery,
    incidentId: selectedIncidentId || undefined,
    skip: (currentPage - 1) * itemsPerPage,
    take: itemsPerPage,
  });

  const items: FamilyAssistance[] = data?.items || [];
  const deleteMutation = useDeleteFamilyAssistance();
  const navigate = useNavigate();

  const [assistanceToDelete, setAssistanceToDelete] = useState<FamilyAssistance | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const incidentsQuery = useInfiniteIncidents({
    take: INCIDENTS_PER_PAGE,
    name: incidentFilter,
  });

  const handleItemsPerPageChange = (newItemsPerPage: number): void => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    refetch();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleEdit = (id: string) => {
    navigate(`/vadmin/family-assistance/edit/${id}`);
  };

  const openDeleteDialog = (item: FamilyAssistance) => {
    setAssistanceToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (assistanceToDelete?.id) {
      try {
        await deleteMutation.mutateAsync({ id: assistanceToDelete.id });
        setIsDeleteDialogOpen(false);
        showToast('Record deleted successfully', 'success');
        refetch();
      } catch (error) {
        showToast('Failed to delete record', 'error');
      }
    }
  };

  return (
    <div className="family-assistance-entries">
      <div className="emc-page-header">
        <div className="emc-page-header__breadcrumbs">
          <Breadcrumbs />
        </div>
        <div className="header-content">
          <h3 className="page-heading">
            Family Assistance Requests
            <div className="divider" />
            <div className="incident-dropdown">
              <InfiniteDropdown
                value={selectedIncidentId}
                onChange={(_, data) => {
                  setSelectedIncidentId(data.value);
                  setCurrentPage(1);
                  refetch();
                }}
                onFilterChange={setIncidentFilter}
                queryResult={incidentsQuery}
                itemToOption={(incident: Incident) => ({
                  value: incident.id ?? "",
                  text: incident.name ?? ""
                })}
                placeholder="All Incidents"
                clearable
              />
            </div>
            <div className="search-field">
              <Field label="Search by Name">
                <Input
                  type="text"
                  contentBefore={<SearchFilled />}
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </Field>
            </div>
          </h3>
        </div>
      </div>

      <div className="list-cards">
        <LoadingWrapper
          isLoading={isLoading}
          isEmpty={data?.totalCount === 0}
          emptyMessage={`No family assistance requests found`}
          emptyIcon={<CalendarPersonRegular />}
        >
          {items.map((item: FamilyAssistance) => (
            <div key={item.id} className="large-card">
              <div className="large-card__content">
                <div className="person-card__content">
                  <div className="person-image">
                    <LazyImage
                      src={item.photoUrl}
                      alt={`${item.firstName} ${item.lastName}`}
                      fallback={<div className="person-image--no-image" />}
                    />
                  </div>
                  <div className="person-info">
                    <div className="person-header">
                      <span className='large-card__title'>
                        {item.firstName} {item.lastName}
                      </span>
                    </div>
                    <div className="person-details">
                      <div>
                        <span className='large-card__label'>Looking For:</span> {item.lookingForFirstName} {item.lookingForLastName}
                      </div>
                      <div>
                        <span className='large-card__label'>Number:</span> {item.phoneNumber}
                      </div>
                      <div>
                        <span className='large-card__label'>Email:</span> {item.email}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="large-card__actions">
                <Button
                  className="icon-button"
                  icon={<Edit20Filled />}
                  appearance="subtle"
                  onClick={() => handleEdit(item.id)}
                />
                <Button
                  className="icon-button"
                  icon={<Delete20Filled />}
                  appearance="subtle"
                  onClick={() => openDeleteDialog(item)}
                />
              </div>
            </div>
          ))}
          <Paginator
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(newPage) => {
              setCurrentPage(newPage);
              refetch();
            }}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalCount}
            currentItems={(currentPage - 1) * itemsPerPage + 1}
            showItemsPerPage={true}
          />
        </LoadingWrapper>
      </div>

      <DialogBox
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete Family Assistance Request"
        primaryButtonText="Delete"
        onPrimaryButtonClick={handleConfirmDelete}
        isPrimaryButtonLoading={deleteMutation.isPending}
      >
        {assistanceToDelete && (
          <>Are you sure you want to delete record {assistanceToDelete.firstName} {assistanceToDelete.lastName}?</>
        )}
      </DialogBox>
    </div>
  );
};

export default FamilyAssistanceEntries;
